import React, { forwardRef } from 'react';
import { Select } from 'antd';
import CustomInput from '../../../../../../components/input/Input';
import CustomInputNumber from '../../../../../../components/input/InputNumber';
import Switch from '../../../../../../components/input/Switch';
import { CustomAttribute } from '../../../../../../types/product/customAttribute';

interface DynamicInputProps {
  attribute: CustomAttribute;
  isEditable: boolean;
  value?: string | string[] | number | boolean | null;
  // getFieldDecorator에서 항상 주입하고 있지만 타입체크 통과를 위해 옵셔널로 설정
  onChange?: (value: string | string[] | number | boolean | null) => void;
}

/**
 * controlType과 dataType에 따라 적절한 입력 컴포넌트를 렌더링
 * antd getFieldDecorator로 감싸져서 validation이 자동 처리됩니다
 * forwardRef: getFieldDecorator가 ref를 전달하므로 필요
 */
const DynamicInput = forwardRef<HTMLDivElement, DynamicInputProps>(
  ({ attribute, isEditable, value: formValue, onChange }, ref) => {
    const { controlType, dataType, options = [], nullable } = attribute;

    // Single Select
    if (controlType === 'single-select') {
      if (!options.length) {
        return <div>옵션이 없습니다.</div>;
      }
      return (
        <Select
          className="amondz-select"
          value={formValue as string | null}
          onChange={(val) => onChange?.(val ?? null)}
          disabled={!isEditable}
          allowClear={nullable}
          placeholder={nullable ? '선택하세요 (선택사항)' : '선택하세요'}
          size="large"
          style={{ width: '100%' }}
        >
          {options.map((option) => (
            <Select.Option key={option.code} value={option.code}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );
    }

    // Multi Select
    if (controlType === 'multi-select') {
      if (!options.length) {
        return <div>옵션이 없습니다.</div>;
      }
      return (
        <Select
          className="amondz-select"
          mode="multiple"
          value={(formValue as string[]) || []}
          onChange={(val) => onChange?.(val)}
          disabled={!isEditable}
          allowClear={nullable}
          placeholder={nullable ? '선택하세요 (선택사항)' : '선택하세요'}
          size="large"
          style={{ width: '100%' }}
        >
          {options.map((option) => (
            <Select.Option key={option.code} value={option.code}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );
    }

    // Input (Number or String)
    if (controlType === 'input') {
      // Number Input
      if (dataType.type === 'number') {
        return (
          <CustomInputNumber
            value={formValue as number | undefined}
            onChange={(val) => onChange?.(val ?? null)}
            isEditable={isEditable}
            placeholder={nullable ? '입력하세요 (선택사항)' : '입력하세요'}
            style={{ width: '100%' }}
          />
        );
      }

      // String Input
      if (dataType.type === 'string') {
        return (
          <CustomInput
            value={formValue as string | undefined}
            onChange={(val) => onChange?.(val)}
            isEditable={isEditable}
            placeholder={nullable ? '입력하세요 (선택사항)' : '입력하세요'}
            style={{ width: '100%' }}
          />
        );
      }
    }

    // Switch
    if (controlType === 'switch') {
      return <Switch checked={formValue as boolean} onChange={(val) => onChange?.(val)} disabled={!isEditable} />;
    }

    // Default: 지원하지 않는 입력 타입
    return <div ref={ref}>지원하지 않는 입력 타입입니다.</div>;
  },
);

DynamicInput.displayName = 'DynamicInput';

export default DynamicInput;
